import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import { GetMemberDocuments, MemberDocumentSummary } from '../../services/dtos';

// D3 member document area. The server runs the full gate stack on every call
// (auth → good standing → step-up elevation); this page just translates the
// two structured 403s into UX: StepUpRequired bounces to /verify and
// NotGoodStanding renders the office-contact message.

export function docsApiError(
  e: any,
  navigate: (to: string) => void,
  returnTo: string
): { blockedMessage?: string; error?: string } | null {
  const code = e?.responseStatus?.errorCode;
  if (code === 'StepUpRequired') {
    navigate(`/verify?returnTo=${encodeURIComponent(returnTo)}`);
    return null;
  }
  if (code === 'NotGoodStanding')
    return { blockedMessage: e?.responseStatus?.message };
  return { error: e?.responseStatus?.message || 'Loading documents failed — try again.' };
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { dateStyle: 'medium' });
}

// D5 due dates are date-only values stored at UTC midnight — format and
// compare them in UTC so the shown day never drifts by timezone.
function fmtDueDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString([], { dateStyle: 'medium', timeZone: 'UTC' });
}

function isPastDue(iso: string): boolean {
  const d = new Date(iso);
  return !isNaN(d.getTime()) && Date.now() >= d.getTime() + 24 * 3600 * 1000;
}

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<MemberDocumentSummary[] | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiServices.client
      .get(new GetMemberDocuments())
      .then((data: MemberDocumentSummary[]) => setDocs(data))
      .catch((e: any) => {
        const r = docsApiError(e, navigate, '/docs');
        if (r?.blockedMessage) setBlockedMessage(r.blockedMessage);
        else if (r?.error) setError(r.error);
      });
  }, []);

  return (
    <div className="mx-auto w-full max-w-3xl flex flex-col gap-6 dark:text-white">
      <div>
        <h2 className="text-2xl font-bold">Union Documents</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Contracts and policies posted by the local. Pages are watermarked with
          your name — please don&apos;t share them.
        </p>
      </div>

      {error && <Toast color="failure">{error}</Toast>}

      {blockedMessage && (
        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900 p-4 text-sm text-yellow-800 dark:text-yellow-200">
          {blockedMessage}
        </div>
      )}

      {!docs && !error && !blockedMessage && <Spinner color="info" />}

      {docs && docs.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No documents have been posted yet.
        </div>
      )}

      {docs && docs.length > 0 && (
        <div className="flex flex-col gap-3">
          {docs.map((d) => (
            <Link
              key={d.id}
              to={`/docs/${d.id}`}
              className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 hover:border-indigo-400 dark:hover:border-indigo-500"
            >
              <div className="font-medium text-gray-900 dark:text-white break-words">
                {d.title}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                {d.docType && (
                  <span className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 font-medium">
                    {d.docType}
                  </span>
                )}
                <span>{d.pageCount} page{d.pageCount === 1 ? '' : 's'}</span>
                <span>Posted {fmtDate(d.createdAtUtc)}</span>
                {(d.reviewMode === 'Acknowledge' || d.reviewMode === 'TypedName') &&
                  (d.acknowledged ? (
                    <span className="rounded bg-green-100 dark:bg-green-900 px-2 py-0.5 font-medium text-green-800 dark:text-green-200">
                      Acknowledged ✓
                    </span>
                  ) : (
                    <span className="rounded bg-amber-100 dark:bg-amber-900 px-2 py-0.5 font-medium text-amber-800 dark:text-amber-200">
                      Acknowledgment required
                    </span>
                  ))}
                {/* D5: the due date informs — it never gates anything */}
                {d.dueAtUtc &&
                  (d.reviewMode === 'Acknowledge' || d.reviewMode === 'TypedName') &&
                  !d.acknowledged && (
                    <span
                      className={`rounded px-2 py-0.5 font-medium ${
                        isPastDue(d.dueAtUtc)
                          ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          : 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200'
                      }`}
                    >
                      Due {fmtDueDate(d.dueAtUtc)}
                    </span>
                  )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
